import { ChangeDetectionStrategy, Component, DestroyRef, effect, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { getAnimationAppearOnTrigger } from '@examdojo/animation';
import { SyllabusQuery } from '@examdojo/category/v2';
import { MasterListViewComponent, provideSimpleListView } from '@examdojo/core/list-view';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { QUESTION_STATUS_TO_LABEL, QUESTION_STATUSES, QuestionStatus } from '@examdojo/models/question';
import { DerivativeQuestionItemsViewStoreModel, QuestionEntityService } from '@examdojo/question';
import { DerivativeQuestionHistoryDialogService } from '@examdojo/review';
import { LoaderComponent } from '@examdojo/ui/loader';
import {
  ColDef,
  FilterModel,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowClickedEvent,
  SortModelItem,
  ValueFormatterParams,
} from 'ag-grid-community';
import { EMPTY, finalize, first, tap } from 'rxjs';
import { CmsUrlParts, NEW_QUESTION_ID_PARAM } from '../../cms/cms.model';
import { AgGirdColumnStateManager } from '../../shared/ag-grid/ag-gird-column-state.manager';
import { QuestionListStatusCellRendererComponent } from '../list/question-list-status-cell-renderer.component';
import { QuestionListService } from '../services/question-list.service';

const QUESTION_LIST_VIEW_STATE_STORAGE_KEY = 'scoped-question-grid-state';

@Component({
  selector: 'exam-scoped-question-list',
  templateUrl: './scoped-question-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  imports: [MasterListViewComponent, LoaderComponent],
  providers: [provideSimpleListView(QuestionListService)],
  animations: [getAnimationAppearOnTrigger()],
})
export class ScopedQuestionListComponent {
  constructor(
    private readonly router: Router,
    private readonly questionService: QuestionEntityService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly destroyRef: DestroyRef,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly derivativeQuestionHistoryDialogService: DerivativeQuestionHistoryDialogService,
  ) {
    effect(() => {
      const referenceQuestionId = this.referenceQuestionId();
      if (referenceQuestionId) {
        this.columnStateManager.overrideStorageKey(`${QUESTION_LIST_VIEW_STATE_STORAGE_KEY}-${referenceQuestionId}`);
      }
    });
  }
  readonly agGridAdditionalClass = input<string>('ag-theme-basic-table small-height');
  readonly referenceQuestionId = input<number>();
  readonly gridApi = signal<GridApi<DerivativeQuestionItemsViewStoreModel> | null>(null);
  readonly isLoading = signal<boolean>(true);

  readonly columnDefs: Array<ColDef<DerivativeQuestionItemsViewStoreModel>> = [
    {
      field: 'question_number',
      headerName: 'Question Number',
      valueGetter: ({ data }) => {
        return data?.question_number ?? '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      valueGetter: ({ data }) => {
        return data?.id ?? '';
      },
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      minWidth: 150,
      cellClass: 'truncate',
      cellRenderer: QuestionListStatusCellRendererComponent,
      filterParams: {
        values: QUESTION_STATUSES,
        valueFormatter: (params: ValueFormatterParams<DerivativeQuestionItemsViewStoreModel, string>) =>
          QUESTION_STATUS_TO_LABEL[params.value as QuestionStatus],
      },
    },
  ];

  readonly serverSideDatasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      const { startRow, endRow, sortModel, filterModel } = params.request;
      const sortBy: SortModelItem[] = sortModel.length ? sortModel : [{ colId: 'updated_at', sort: 'desc' }];

      this.questionService
        .fetchPaginatedListView({
          startRow,
          endRow: endRow ? endRow - 1 : undefined,
          sortBy: sortBy,
          filterBy: {
            ...filterModel,
            reference_question_id: {
              filterType: 'set',
              values: [this.referenceQuestionId()],
            },
          } as FilterModel,
        })
        .pipe(
          first(),
          tap({
            next: (data) => {
              params.success({ rowData: data.data, rowCount: data.totalCount });
              this.columnStateManager.initialRowsLoaded.set(true);
            },
            error: () => {
              params.fail();
            },
          }),
          this.errorHandlerService.catchError(
            `[QuestionListComponent.getRows]: failed to load the questions`,
            () => EMPTY,
            { toast: `Could not load the questions` },
          ),
          finalize(() => this.isLoading.set(false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    },
  };

  readonly gridOptions: GridOptions<DerivativeQuestionItemsViewStoreModel> = {
    pagination: true,
    paginationAutoPageSize: true,
    cacheBlockSize: 100,
    rowModelType: 'serverSide',
    serverSideDatasource: this.serverSideDatasource,
    suppressCellFocus: true,
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No questions found',
      body: 'Please try with another filter',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      menuTabs: [],
    },
    context: {
      componentParent: this,
    },
    animateRows: true,
    rowSelection: 'single',
    getContextMenuItems: (event) => [
      {
        name: 'Open in New Tab',
        icon: `<i class="fa-light fa-arrow-up-right-from-square"></i>`,
        action: () => {
          if (!event.node?.data) {
            return;
          }

          const activeSyllabus = this.syllabusQuery.getActive();
          if (!activeSyllabus) {
            return;
          }

          window.open(
            `/${CmsUrlParts.Syllabus}/${activeSyllabus.slug}/${CmsUrlParts.Questions}/${event.node?.data.id}`,
            '_blank',
            'noopener,noreferrer',
          );
        },
      },
      {
        name: 'View History',
        icon: `<i class="fa-light fa-timeline"></i>`,
        action: () => {
          if (!event.node?.data?.id) {
            return;
          }

          this.openQuestionHistoryDialog(event.node.data.id);
        },
      },
    ],

    rowClass: 'cursor-pointer',
    onPaginationChanged: () => {
      this.columnStateManager.saveGridState();
    },
    onSortChanged: () => {
      this.columnStateManager.saveGridState();
    },
    onFilterChanged: () => {
      this.columnStateManager.saveGridState();
    },

    getRowId: (params) => (params.data?.id ? `${params.data.id}` : ''),
  };

  readonly columnStateManager = new AgGirdColumnStateManager(this.gridOptions, QUESTION_LIST_VIEW_STATE_STORAGE_KEY);

  addQuestion() {
    const activeSyllabus = this.syllabusQuery.getActive();
    if (!activeSyllabus) {
      return;
    }

    this.router.navigate([
      '/',
      CmsUrlParts.Syllabus,
      activeSyllabus.slug,
      CmsUrlParts.Questions,
      NEW_QUESTION_ID_PARAM,
    ]);
  }

  openQuestion(event: RowClickedEvent<DerivativeQuestionItemsViewStoreModel, void>) {
    if (!event.data) {
      return;
    }

    const activeSyllabus = this.syllabusQuery.getActive();
    if (!activeSyllabus) {
      return;
    }

    this.router.navigate(['/', CmsUrlParts.Syllabus, activeSyllabus.slug, CmsUrlParts.Questions, event.data.id]);
  }

  async openQuestionHistoryDialog(questionId: number) {
    await this.derivativeQuestionHistoryDialogService.openDialog({
      input: { questionId },
    });
  }

  onGridReady(evt: GridReadyEvent) {
    this.gridApi.set(evt.api);

    this.columnStateManager.gridApi.set(evt.api);
    this.columnStateManager.gridReady.set(true);
    this.columnStateManager.loadGridState();
  }
}
